import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { api } from '../../services/api'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import {
  User,
  Buildings,
  Bell,
  Lock,
  Globe,
  CreditCard,
  Envelope,
  Phone,
  MapPin,
  CheckCircle,
  Warning,
  CircleNotch,
  Camera,
  Trash,
  UsersThree,
  Check,
  CurrencyDollar,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { changePassword, uploadAvatar, deleteAvatar, uploadCompanyLogo, deleteCompanyLogo, updateProfile } from '../../services/auth'
import { useDialog } from '../../context/DialogContext'
import { TeamTab } from '../../components/settings/TeamTab'
import { ImageCropperModal } from '../../components/ImageCropperModal'
import { CNPJLookupInput } from '../../components/cnpj'
import type { CNPJData } from '../../services/brasilapi'

interface CompanyProfile {
  id: string
  name: string
  cnpj: string
  email: string
  phone: string
  address: {
    street: string
    number: string
    complement?: string
    city: string
    state: string
    zipCode: string
  }
}

interface NotificationSettings {
  emailNotifications: {
    newOffer: boolean
    offerExpiring: boolean
    shipmentUpdate: boolean
    documentRequired: boolean
    messageReceived: boolean
  }
  pushNotifications: {
    newOffer: boolean
    shipmentUpdate: boolean
    urgentAlerts: boolean
  }
}

export default function SettingsPage() {
  const { user, refreshProfile } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const { showSuccess, toastSuccess } = useDialog()

  // TODO: Replace with real API data
  const [activeTab, setActiveTab] = useState<string>('profile')
  const [isSaving, setIsSaving] = useState(false)

  // User profile data
  const [userProfile, setUserProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    cargo: user?.cargo || '',
    department: user?.department || '',
  })

  const [companyProfile, setCompanyProfile] = useState<CompanyProfile>({
    id: 'comp1',
    name: 'Empresa Exemplo Ltda',
    cnpj: '12.345.678/0001-90',
    email: 'contato@empresa.com.br',
    phone: '+55 11 3456-7890',
    address: {
      street: 'Av. Paulista',
      number: '1000',
      complement: 'Sala 500',
      city: 'São Paulo',
      state: 'SP',
      zipCode: '01310-100',
    },
  })

  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNotifications: {
      newOffer: true,
      offerExpiring: true,
      shipmentUpdate: true,
      documentRequired: true,
      messageReceived: false,
    },
    pushNotifications: {
      newOffer: true,
      shipmentUpdate: true,
      urgentAlerts: true,
    },
  })

  const [language, setLanguage] = useState('pt-BR')
  const [currency, setCurrency] = useState<'BRL' | 'USD'>('BRL')
  const [currencyLoading, setCurrencyLoading] = useState(false)

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  // Avatar state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar_url || null)
  const [avatarLoading, setAvatarLoading] = useState(false)
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null)
  const [logoCropFile, setLogoCropFile] = useState<File | null>(null)
  const [avatarError, setAvatarError] = useState('')

  // Company logo state
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(user?.current_company?.logo_url || null)
  const [logoLoading, setLogoLoading] = useState(false)
  const [logoError, setLogoError] = useState('')

  // Sync avatar URL when user data changes
  useEffect(() => {
    if (user?.avatar_url) {
      setAvatarUrl(user.avatar_url)
    }
  }, [user?.avatar_url])

  // Sync logo URL when user data changes
  useEffect(() => {
    if (user?.current_company?.logo_url) {
      setLogoUrl(user.current_company.logo_url)
    }
  }, [user?.current_company?.logo_url])

  // Load settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const response = await api.get('/shipper/settings')
        const settings = response.data.settings?.settings
        if (settings?.preferredCurrency) {
          setCurrency(settings.preferredCurrency)
        }
      } catch (err) {
        console.error('Failed to load settings:', err)
      }
    }
    loadSettings()
  }, [])

  // Save preferred currency
  const handleSavePreferredCurrency = useCallback(async (newCurrency: 'BRL' | 'USD') => {
    setCurrencyLoading(true)
    try {
      await api.patch('/shipper/settings', {
        settings: { preferredCurrency: newCurrency }
      })
      setCurrency(newCurrency)
      toastSuccess('Moeda Padrão', `Moeda padrão alterada para ${newCurrency === 'BRL' ? 'Real (R$)' : 'Dólar (US$)'}`)
    } catch (err) {
      console.error('Failed to save preferred currency:', err)
    } finally {
      setCurrencyLoading(false)
    }
  }, [toastSuccess])

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setAvatarError('Tipo de arquivo inválido. Apenas JPEG, PNG, GIF e WebP são permitidos.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Arquivo muito grande. Tamanho máximo é 5MB.')
      return
    }

    setAvatarError('')
    setAvatarCropFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleAvatarCropConfirm = async (croppedFile: File) => {
    setAvatarCropFile(null)
    setAvatarLoading(true)
    setAvatarError('')
    try {
      const result = await uploadAvatar(croppedFile)
      setAvatarUrl(result.avatar_url)
      toastSuccess('Foto Atualizada', 'Sua foto de perfil foi salva com sucesso.')
    } catch (err: any) {
      setAvatarError(err.response?.data?.error || 'Erro ao fazer upload do avatar')
    } finally {
      setAvatarLoading(false)
    }
  }

  const handleAvatarDelete = async () => {
    if (!avatarUrl) return

    setAvatarLoading(true)
    setAvatarError('')

    try {
      await deleteAvatar()
      setAvatarUrl(null)
      toastSuccess('Foto Removida', 'Sua foto de perfil foi removida com sucesso.')
    } catch (err: any) {
      setAvatarError(err.response?.data?.error || 'Erro ao excluir avatar')
    } finally {
      setAvatarLoading(false)
    }
  }

  // Logo upload handlers
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Tipo de arquivo inválido. Apenas JPEG, PNG, GIF, WebP e SVG são permitidos.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('Arquivo muito grande. Tamanho máximo é 5MB.')
      return
    }

    setLogoError('')
    // SVG can't be cropped via canvas — upload as-is
    if (file.type === 'image/svg+xml') {
      void uploadLogoFile(file)
      if (logoInputRef.current) logoInputRef.current.value = ''
      return
    }
    setLogoCropFile(file)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const uploadLogoFile = async (file: File) => {
    setLogoLoading(true)
    setLogoError('')
    try {
      const result = await uploadCompanyLogo(file)
      setLogoUrl(result.logoUrl)
      toastSuccess('Logo Atualizado', 'O logo da empresa foi salvo com sucesso.')
      await refreshProfile()
    } catch (err: any) {
      setLogoError(err.response?.data?.error || 'Erro ao fazer upload do logo')
    } finally {
      setLogoLoading(false)
    }
  }

  const handleLogoDelete = async () => {
    if (!logoUrl) return

    setLogoLoading(true)
    setLogoError('')

    try {
      await deleteCompanyLogo()
      setLogoUrl(null)
      toastSuccess('Logo Removido', 'O logo da empresa foi removido com sucesso.')
      // Refresh profile to update global state
      await refreshProfile()
    } catch (err: any) {
      setLogoError(err.response?.data?.error || 'Erro ao excluir logo')
    } finally {
      setLogoLoading(false)
    }
  }

  // Get user initials for avatar placeholder
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  const handleSaveProfile = async () => {
    setIsSaving(true)
    try {
      await updateProfile({
        name: userProfile.name,
        phone: userProfile.phone,
        cargo: userProfile.cargo,
        department: userProfile.department,
      })
      await refreshProfile()
      showSuccess('Perfil Atualizado', 'Suas informações foram salvas com sucesso.')
    } catch (err: any) {
      console.error('Error saving profile:', err)
      showSuccess('Erro', err.response?.data?.error || 'Erro ao salvar perfil')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCNPJDataConfirmed = (data: CNPJData) => {
    setCompanyProfile((prev) => ({
      ...prev,
      name: data.razao_social || prev.name,
      cnpj: data.cnpj,
      email: data.email?.toLowerCase() || prev.email,
      phone: data.ddd_telefone_1 || prev.phone,
      address: {
        ...prev.address,
        street: data.logradouro ? `${data.tipo_logradouro} ${data.logradouro}` : prev.address.street,
        number: data.numero || prev.address.number,
        complement: data.complemento || prev.address.complement,
        city: data.municipio || prev.address.city,
        state: data.uf || prev.address.state,
        zipCode: data.cep || prev.address.zipCode,
      },
    }))
  }

  const handleSaveCompany = async () => {
    setIsSaving(true)
    // TODO: Implement API call to save company profile
    setTimeout(() => {
      setIsSaving(false)
      showSuccess('Empresa Atualizada', 'Os dados da empresa foram salvos com sucesso.')
    }, 1000)
  }

  const handleSaveNotifications = async () => {
    setIsSaving(true)
    // TODO: Implement API call to save notification preferences
    setTimeout(() => {
      setIsSaving(false)
      showSuccess('Notificações', 'Suas preferências de notificação foram atualizadas.')
    }, 1000)
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Preencha todos os campos')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não conferem')
      return
    }

    setPasswordLoading(true)

    try {
      await changePassword(currentPassword, newPassword)
      setPasswordSuccess('Senha alterada com sucesso!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Erro ao alterar senha')
    } finally {
      setPasswordLoading(false)
    }
  }

  const tabs = [
    { id: 'profile', label: 'Meu Perfil', icon: User },
    { id: 'company', label: 'Empresa', icon: Buildings },
    { id: 'team', label: 'Equipe', icon: UsersThree },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'security', label: 'Segurança', icon: Lock },
    { id: 'preferences', label: 'Preferências', icon: Globe },
    { id: 'billing', label: 'Faturamento', icon: CreditCard },
  ]

  return (
    <AppLayout>
      {avatarCropFile && (
        <ImageCropperModal
          file={avatarCropFile}
          title="Ajustar foto de perfil"
          aspect={1}
          cropShape="round"
          outputSize={512}
          outputMime="image/jpeg"
          onCancel={() => setAvatarCropFile(null)}
          onConfirm={handleAvatarCropConfirm}
        />
      )}
      {logoCropFile && (
        <ImageCropperModal
          file={logoCropFile}
          title="Ajustar logo da empresa"
          aspect={1}
          cropShape="rect"
          outputSize={1024}
          outputMime="image/png"
          onCancel={() => setLogoCropFile(null)}
          onConfirm={(cropped) => { setLogoCropFile(null); void uploadLogoFile(cropped) }}
        />
      )}
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {/* TODO: Add i18n key */ 'Configurações'}
              </h1>
              <p className="text-sm text-gray-500">
                {/* TODO: Add i18n key */ 'Gerencie suas preferências e informações da conta'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <Card>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {tabs.map((tab) => {
                    const IconComponent = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? 'bg-primary text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        style={
                          activeTab === tab.id
                            ? { backgroundColor: 'rgb(5, 150, 105)' }
                            : {}
                        }
                      >
                        <IconComponent size={20} />
                        {tab.label}
                      </button>
                    )
                  })}
                </nav>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* User Profile Tab */}
            {activeTab === 'profile' && (
              <Card>
                <CardHeader>
                  <CardTitle>Informações Pessoais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Avatar Section */}
                  <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden border-4 border-emerald-200">
                        {avatarLoading ? (
                          <CircleNotch size={32} className="animate-spin text-emerald-600" />
                        ) : avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl font-semibold text-emerald-600">
                            {getInitials(userProfile.name || 'U')}
                          </span>
                        )}
                      </div>
                      {/* Camera button overlay */}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={avatarLoading}
                        className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white hover:bg-emerald-700 transition-colors shadow-lg"
                        title="Alterar foto"
                      >
                        <Camera size={16} weight="bold" />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">Foto do Perfil</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Formatos aceitos: JPEG, PNG, GIF ou WebP. Tamanho máximo: 2MB.
                      </p>

                      {avatarError && (
                        <p className="text-sm text-red-600 mt-2">{avatarError}</p>
                      )}

                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={avatarLoading}
                        >
                          <Camera size={16} className="mr-2" />
                          {avatarUrl ? 'Alterar foto' : 'Adicionar foto'}
                        </Button>
                        {avatarUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAvatarDelete}
                            disabled={avatarLoading}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash size={16} className="mr-2" />
                            Remover
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        value={userProfile.name}
                        onChange={(e) =>
                          setUserProfile({ ...userProfile, name: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cargo/Função
                      </label>
                      <input
                        type="text"
                        value={userProfile.cargo}
                        onChange={(e) =>
                          setUserProfile({ ...userProfile, cargo: e.target.value })
                        }
                        placeholder="Ex: Gerente de Logística, Analista Comercial"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Departamento
                      </label>
                      <select
                        value={userProfile.department}
                        onChange={(e) =>
                          setUserProfile({ ...userProfile, department: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                      >
                        <option value="">Selecione...</option>
                        <option value="comercial">Comercial</option>
                        <option value="pricing">Pricing</option>
                        <option value="operacional">Operacional</option>
                        <option value="documentacao">Documentação</option>
                        <option value="financeiro">Financeiro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-mail
                      </label>
                      <div className="relative">
                        <Envelope
                          size={20}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="email"
                          value={userProfile.email}
                          onChange={(e) =>
                            setUserProfile({ ...userProfile, email: e.target.value })
                          }
                          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone
                      </label>
                      <div className="relative">
                        <Phone
                          size={20}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="tel"
                          value={userProfile.phone}
                          onChange={(e) =>
                            setUserProfile({ ...userProfile, phone: e.target.value })
                          }
                          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className="gap-2"
                      style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                    >
                      <CheckCircle size={20} />
                      {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Company Profile Tab */}
            {activeTab === 'company' && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados da Empresa</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Company Logo Section */}
                  <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-lg bg-emerald-100 flex items-center justify-center overflow-hidden border-4 border-emerald-200">
                        {logoLoading ? (
                          <CircleNotch size={32} className="animate-spin text-emerald-600" />
                        ) : logoUrl ? (
                          <img
                            src={logoUrl}
                            alt="Logo da empresa"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Buildings size={40} className="text-emerald-600" />
                        )}
                      </div>
                      {/* Camera button overlay */}
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoLoading}
                        className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white hover:bg-emerald-700 transition-colors shadow-lg"
                        title="Alterar logo"
                      >
                        <Camera size={16} weight="bold" />
                      </button>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">Logo da Empresa</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Formatos aceitos: JPEG, PNG, GIF, WebP ou SVG. Tamanho máximo: 5MB.
                      </p>

                      {logoError && (
                        <p className="text-sm text-red-600 mt-2">{logoError}</p>
                      )}

                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={logoLoading}
                        >
                          <Camera size={16} className="mr-2" />
                          {logoUrl ? 'Alterar logo' : 'Adicionar logo'}
                        </Button>
                        {logoUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleLogoDelete}
                            disabled={logoLoading}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash size={16} className="mr-2" />
                            Remover
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Razão Social
                      </label>
                      <input
                        type="text"
                        value={companyProfile.name}
                        onChange={(e) =>
                          setCompanyProfile({ ...companyProfile, name: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <CNPJLookupInput
                      value={companyProfile.cnpj}
                      onChange={(value) =>
                        setCompanyProfile({ ...companyProfile, cnpj: value })
                      }
                      onDataConfirmed={handleCNPJDataConfirmed}
                      label="CNPJ"
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-mail Corporativo
                      </label>
                      <input
                        type="email"
                        value={companyProfile.email}
                        onChange={(e) =>
                          setCompanyProfile({ ...companyProfile, email: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone Corporativo
                      </label>
                      <input
                        type="tel"
                        value={companyProfile.phone}
                        onChange={(e) =>
                          setCompanyProfile({ ...companyProfile, phone: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin size={20} />
                      Endereço
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CEP
                        </label>
                        <input
                          type="text"
                          value={companyProfile.address.zipCode}
                          onChange={(e) =>
                            setCompanyProfile({
                              ...companyProfile,
                              address: { ...companyProfile.address, zipCode: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Rua/Avenida
                        </label>
                        <input
                          type="text"
                          value={companyProfile.address.street}
                          onChange={(e) =>
                            setCompanyProfile({
                              ...companyProfile,
                              address: { ...companyProfile.address, street: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Número
                        </label>
                        <input
                          type="text"
                          value={companyProfile.address.number}
                          onChange={(e) =>
                            setCompanyProfile({
                              ...companyProfile,
                              address: { ...companyProfile.address, number: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Complemento
                        </label>
                        <input
                          type="text"
                          value={companyProfile.address.complement || ''}
                          onChange={(e) =>
                            setCompanyProfile({
                              ...companyProfile,
                              address: { ...companyProfile.address, complement: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cidade
                        </label>
                        <input
                          type="text"
                          value={companyProfile.address.city}
                          onChange={(e) =>
                            setCompanyProfile({
                              ...companyProfile,
                              address: { ...companyProfile.address, city: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <input
                          type="text"
                          value={companyProfile.address.state}
                          onChange={(e) =>
                            setCompanyProfile({
                              ...companyProfile,
                              address: { ...companyProfile.address, state: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleSaveCompany}
                      disabled={isSaving}
                      className="gap-2"
                      style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                    >
                      <CheckCircle size={20} />
                      {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Tab */}
            {activeTab === 'team' && <TeamTab />}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <Card>
                <CardHeader>
                  <CardTitle>Preferências de Notificação</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Notificações por E-mail
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(notifications.emailNotifications).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between py-2">
                          <label className="text-sm font-medium text-gray-700">
                            {key === 'newOffer' && 'Nova oferta recebida'}
                            {key === 'offerExpiring' && 'Oferta próxima do vencimento'}
                            {key === 'shipmentUpdate' && 'Atualização de embarque'}
                            {key === 'documentRequired' && 'Documento necessário'}
                            {key === 'messageReceived' && 'Nova mensagem recebida'}
                          </label>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) =>
                                setNotifications({
                                  ...notifications,
                                  emailNotifications: {
                                    ...notifications.emailNotifications,
                                    [key]: e.target.checked,
                                  },
                                })
                              }
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-200 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Notificações Push
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(notifications.pushNotifications).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between py-2">
                          <label className="text-sm font-medium text-gray-700">
                            {key === 'newOffer' && 'Nova oferta recebida'}
                            {key === 'shipmentUpdate' && 'Atualização de embarque'}
                            {key === 'urgentAlerts' && 'Alertas urgentes'}
                          </label>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) =>
                                setNotifications({
                                  ...notifications,
                                  pushNotifications: {
                                    ...notifications.pushNotifications,
                                    [key]: e.target.checked,
                                  },
                                })
                              }
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-200 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleSaveNotifications}
                      disabled={isSaving}
                      className="gap-2"
                      style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                    >
                      <CheckCircle size={20} />
                      {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <Card>
                <CardHeader>
                  <CardTitle>Segurança</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Alterar Senha
                    </h3>

                    {passwordError && (
                      <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                        {passwordError}
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                        {passwordSuccess}
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Senha Atual
                        </label>
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Digite sua senha atual"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nova Senha
                        </label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Mínimo 6 caracteres"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirmar Nova Senha
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Digite a nova senha novamente"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Autenticação de Dois Fatores
                    </h3>
                    <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-3">
                        <Warning size={24} className="text-yellow-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Autenticação de dois fatores não configurada
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            Adicione uma camada extra de segurança à sua conta
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        {/* TODO: Implement 2FA setup */}
                        Configurar
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <Button
                      className="gap-2"
                      style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                      onClick={handleChangePassword}
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? (
                        <>
                          <CircleNotch size={20} className="animate-spin" />
                          Atualizando...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={20} />
                          Atualizar Senha
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Preferências do Sistema</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Idioma
                      </label>
                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="pt-BR">Português (Brasil)</option>
                        <option value="en-US">English (US)</option>
                        <option value="es-ES">Español</option>
                      </select>
                    </div>
                  </CardContent>
                </Card>

                {/* Currency Preference */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CurrencyDollar size={20} />
                      Moeda Padrão
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Moeda para Visualização de Valores
                        </label>
                        <p className="text-sm text-gray-500 mb-4">
                          Defina a moeda de preferência para visualização de valores nas ofertas recebidas
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* BRL Option */}
                          <button
                            onClick={() => handleSavePreferredCurrency('BRL')}
                            disabled={currencyLoading}
                            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                              currency === 'BRL'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {currency === 'BRL' && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-emerald-600" />
                              </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-1">Real (R$)</h4>
                            <p className="text-sm text-gray-500">
                              Valores em Reais com conversão para Dólar
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Ideal para empresas brasileiras
                            </p>
                          </button>

                          {/* USD Option */}
                          <button
                            onClick={() => handleSavePreferredCurrency('USD')}
                            disabled={currencyLoading}
                            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                              currency === 'USD'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {currency === 'USD' && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-emerald-600" />
                              </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-1">Dólar (US$)</h4>
                            <p className="text-sm text-gray-500">
                              Valores em Dólar com conversão para Real
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Padrão internacional de frete
                            </p>
                          </button>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Como funciona:</strong> Ao visualizar ofertas, os valores serão exibidos na moeda selecionada
                          como principal, com a conversão para a outra moeda exibida abaixo.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <Card>
                <CardHeader>
                  <CardTitle>Faturamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <CreditCard size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 font-medium mb-2">
                      {/* TODO: Implement billing features */}
                      Recursos de faturamento em desenvolvimento
                    </p>
                    <p className="text-sm text-gray-400">
                      Em breve você poderá gerenciar métodos de pagamento,
                      <br />
                      visualizar faturas e histórico de transações
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
